import * as t from 'io-ts';
import {debounce, difference, isArray, isFunction, isObject, isUndefined} from 'lodash';
import memoize from 'memoize-one';
import {Store} from 'redux';

import {electronAppVersionGTE} from '../../../libs/shared/helpers/src/environment/platformHelpers';
import {isPromise} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {htmlToText} from '../../../srcCommon/services/converters/htmlToTextConverter';
import {readApiModel} from '../../helpers/apiHelpers';
import {makeApiType} from '../../models/api/apiTypes';
import {CommentStatusesEnum} from '../../models/api/commentModel';
import {MessageVisualStatusesEnum} from '../../models/api/messageModel';
import {AppState} from '../../store/appState';

/*
 * Front => Electron.
 */

let currentWindowBridge: any;
let currentLocalBridge: any;

export function setupElectronBridgeOutbound(store: Store<AppState>, windowBridge: any, localBridge: any) {
  currentWindowBridge = windowBridge;
  currentLocalBridge = localBridge;

  store.subscribe(() => {
    const state = store.getState();
    maybeDetectSpellcheckLanguageInMessage(state);
    maybeDetectSpellcheckLanguageInComment(state);
  });

  let previousOfflineDraftMessageUids: ReadonlyArray<string> = [];
  function maybeDetectSpellcheckLanguageInMessage(state: AppState) {
    // Find the messages that started saving in the last update.
    const {offlineDraftMessageUids, messages} = state.data;
    const removedDraftMessageUids = difference(offlineDraftMessageUids, previousOfflineDraftMessageUids);
    previousOfflineDraftMessageUids = offlineDraftMessageUids;

    // If no messages were found, nothing to do.
    const lastSavedMessageUid = removedDraftMessageUids[0];
    const lastSavedMessage = lastSavedMessageUid && messages[lastSavedMessageUid];
    if (!lastSavedMessage || lastSavedMessage.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
      return;
    }

    // Otherwise, use the message for spellcheck detection.
    detectSpellcheckLanguage(lastSavedMessage.text || htmlToText(lastSavedMessage.html));
  }

  let previousOfflineDraftCommentUids: ReadonlyArray<string> = [];
  function maybeDetectSpellcheckLanguageInComment(state: AppState) {
    // Find the comments that started saving in the last update.
    const {offlineDraftCommentUids, comments} = state.data;
    const removedDraftCommentUids = difference(offlineDraftCommentUids, previousOfflineDraftCommentUids);
    previousOfflineDraftCommentUids = offlineDraftCommentUids;

    // If no comments were found, nothing to do.
    const lastSavedCommentUid = removedDraftCommentUids[0];
    const lastSavedComment = lastSavedCommentUid && comments.byId[lastSavedCommentUid];
    if (!lastSavedComment || lastSavedComment.status !== CommentStatusesEnum.DRAFT) {
      return;
    }

    // Otherwise, use the comment for spellcheck detection.
    detectSpellcheckLanguage(lastSavedComment.text);
  }

  const detectSpellcheckLanguage = debounce(async (text: string) => {
    // Validate the text.
    if (text.length < 8) {
      return;
    }

    // Detect the language.
    const textEnd = text.slice(-256);
    const promise = windowBridge.detectSpellcheckLanguageFromText(textEnd);
    if (!isPromise(promise)) {
      return;
    }

    // If the language has changed, clear the spellcheck cache.
    const wasLanguageUpdated = await promise;
    if (!wasLanguageUpdated || !isFunction(localBridge.languageUpdated)) {
      return;
    }

    // We need to clear it multiple times as the underlying component has its own cache.
    localBridge.languageUpdated();
    setTimeout(() => localBridge.languageUpdated(), 2500);
    setTimeout(() => localBridge.languageUpdated(), 5000);
  }, 1000);
}

function runOnWindowBridge<T>(worker: (bridge: any) => T) {
  if (!currentWindowBridge) {
    reportToBugsnag(new Error('Missing window bridge.'));
    return undefined;
  }

  return worker(currentWindowBridge);
}

function runOnLocalBridge(worker: (bridge: any) => void) {
  if (!currentLocalBridge) {
    reportToBugsnag(new Error('Missing local bridge.'));
    return;
  }

  worker(currentLocalBridge);
}

const canInterrogateWindowBridge = memoize(() => electronAppVersionGTE('3.26.0'));

export function electronWindowBridgeHas(prop: string): boolean {
  return withElectronWindowBridgeHaving(prop, () => true) ?? false;
}

export function withElectronWindowBridgeHaving<T>(
  propOrProps: string | ReadonlyArray<string>,
  callback: (windowBridge: any) => T,
): T | undefined {
  if (!canInterrogateWindowBridge()) {
    return undefined;
  }

  const props = isArray(propOrProps) ? propOrProps : [propOrProps];

  return runOnWindowBridge((windowBridge) => {
    if (props.some((prop) => isUndefined(windowBridge[prop]))) {
      return undefined;
    }

    return callback(windowBridge);
  });
}

/*
 * API.
 */

export function showNotificationUsingElectron(options: NotificationOptions, link: string) {
  runOnWindowBridge((bridge) => {
    bridge.showNotification({...options, link});
  });
}

export function focusMainWindow() {
  runOnWindowBridge((bridge) => {
    bridge.focusMainWindow();
  });
}

export function openExternalUrl(url: string) {
  runOnWindowBridge((bridge) => {
    bridge.openExternalUrl(url);
  });
}

export enum ElectronDownloadItemTypesEnum {
  DOWNLOAD = 'download',
  PREVIEW = 'preview',
}

export function downloadItem(
  url: string,
  type: ElectronDownloadItemTypesEnum,
  name?: string,
  shouldPromptForPath?: boolean,
) {
  runOnWindowBridge((bridge) => {
    bridge.downloadItem(url, type, name, shouldPromptForPath);
  });
}

export function cancelDownload(url: string) {
  runOnWindowBridge((bridge) => {
    bridge.cancelDownload(url);
  });
}

export function updateBadge(badgeValue: string) {
  runOnWindowBridge((bridge) => {
    bridge.updateBadge(badgeValue);
  });
}

export function setAsDefaultEmailClient() {
  runOnWindowBridge((bridge) => {
    bridge.registerDefaultEmailClient();
  });
}

export function isDefaultEmailClient() {
  return new Promise<boolean>((resolve) => {
    runOnWindowBridge((bridge) => {
      bridge.checkDefaultEmailClient().then((value: boolean) => {
        resolve(value);
      });
    });
  });
}

export function currentDownloadLocation() {
  return new Promise<string>((resolve) => {
    runOnWindowBridge((bridge) => {
      resolve(bridge.currentDownloadLocation());
    });
  });
}

export function updateDownloadLocation() {
  return new Promise<void>((resolve) => {
    runOnWindowBridge((bridge) => {
      bridge.updateDownloadLocation().then(resolve);
    });
  });
}

export function quitAndInstall() {
  runOnWindowBridge((bridge) => {
    bridge.quitAndInstall();
  });
}

export function userSignedOut() {
  runOnLocalBridge((bridge) => {
    bridge.appLoading();
  });

  runOnWindowBridge((bridge) => {
    bridge.userSignedOut();
  });
}

export function openElectronAuthPopup(url: string) {
  if (!electronAppVersionGTE('3.21.5')) {
    return false;
  }

  runOnWindowBridge((bridge) => {
    bridge.openAuthPopup(url);
  });
  return true;
}

export async function getElectronMemoryAsync() {
  if (!electronAppVersionGTE('3.21.3')) {
    return undefined;
  }

  if (!currentLocalBridge) {
    reportToBugsnag(new Error('Missing local bridge.'));
    return undefined;
  }

  return await currentLocalBridge.getElectronMemory();
}

function isUint8Array(value: unknown): value is Uint8Array {
  return isObject(value) && value.constructor?.name === 'Uint8Array';
}

const RUint8Array = new t.Type<Uint8Array>(
  'Uint8Array',
  isUint8Array,
  (u, c) => (isUint8Array(u) ? t.success(u) : t.failure(u, c)),
  t.identity,
);

const RClipboardFile = t.type({
  name: t.string,
  type: t.union([t.string, t.undefined]),
  data: RUint8Array,
});

const RClipboardFiles = t.array(RClipboardFile);

interface ClipboardFilesModel extends t.TypeOf<typeof RClipboardFiles> {}

function parseClipboardFiles(src: any): ClipboardFilesModel {
  return src;
}

export const ApiClipboardFiles = makeApiType('clipboardFiles', RClipboardFiles, parseClipboardFiles);

export function getFilesFromClipboardAsync(): Promise<File[]> | undefined {
  if (!electronAppVersionGTE('3.25.1')) {
    return undefined;
  }

  return runOnWindowBridge(async (bridge) => {
    const rawClipboardFiles = await bridge.getFilesFromClipboard();
    const clipboardFiles = readApiModel(ApiClipboardFiles, rawClipboardFiles);
    return clipboardFiles.map((file) => new File([file.data], file.name, {type: file.type || ''}));
  });
}

const RExternalShareFiles = t.array(
  t.type({
    name: t.string,
    data: RUint8Array,
  }),
);

interface ExternalShareFilesModel extends t.TypeOf<typeof RExternalShareFiles> {}

function parseExternalShareFiles(src: any): ExternalShareFilesModel {
  return src;
}

const ApiExternalShareFiles = makeApiType('externalShareFiles', RExternalShareFiles, parseExternalShareFiles);

export async function fetchMostRecentExternalShareFiles() {
  return await withElectronWindowBridgeHaving('fetchMostRecentExternalShareFiles', async (bridge) => {
    const rawExternalShareFiles = await bridge.fetchMostRecentExternalShareFiles();
    return readApiModel(ApiExternalShareFiles, rawExternalShareFiles);
  });
}

export interface ElectronPoint {
  x: number;
  y: number;
}

export async function setMacOsTrafficLightPositionAsync(position: ElectronPoint | null): Promise<void> {
  await withElectronWindowBridgeHaving('setTrafficLightPosition', async (bridge) => {
    await bridge.setTrafficLightPosition(position);
  });
}

export async function hideElectronDragRegionsAsync(): Promise<void> {
  await withElectronWindowBridgeHaving('hideDragRegions', async (bridge) => {
    await bridge.hideDragRegions();
  });
}

export async function hideWindowsTitleBar(): Promise<void> {
  return runOnWindowBridge((bridge) => {
    bridge.hideWindowsTitleBar();
  });
}

export async function openApplicationMenu(cursorOffset: ElectronPoint): Promise<void> {
  return runOnWindowBridge((bridge) => {
    bridge.openApplicationMenu(cursorOffset);
  });
}

export async function handleTitleBarDoubleClickAsync(): Promise<void> {
  await withElectronWindowBridgeHaving('handleTitleBarDoubleClick', async (bridge) => {
    await bridge.handleTitleBarDoubleClick();
  });
}

export async function minimizeWindow(): Promise<void> {
  return runOnWindowBridge((bridge) => {
    bridge.minimize();
  });
}

export async function maximizeWindow(): Promise<void> {
  return runOnWindowBridge((bridge) => {
    bridge.maximize();
  });
}

export async function unmaximizeWindow(): Promise<void> {
  return runOnWindowBridge((bridge) => {
    bridge.unmaximize();
  });
}

export async function closeWindow(): Promise<void> {
  return runOnWindowBridge((bridge) => {
    bridge.close();
  });
}

export async function clearElectronCache(): Promise<void> {
  await withElectronWindowBridgeHaving('clearCache', async (bridge) => {
    await bridge.clearCache();
  });
}
